import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountStatus, AppButton, BigAddInvestmentIcon, BigDisabledAddInvestmentIcon, Box, ChevronRightIcon, costFormater, Hint, Inscription, Preloader, RawButton, Text, useResponsive, WarningIcon } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { Roles } from '@/common/constants';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { useInvestor } from '@/modules/investments/hooks';
import { InvestmentResumeBlock } from '@/modules/investments/Investments';
import { InvestorStatus } from '@/modules/investments/investments.types';
import ServerErrorModal from '@/modules/investments/modal/ServerErrorModal';
import { useInvestmentsStartQuery } from '@/modules/investments/queries';
import { useMyEnrollments } from '@/modules/transaction';
import baseTheme from '@/styles/theme';

import { useCIPInfo } from '../cip/hooks';
import useIsFullLiquidation from '../hooks/useIsFullLiquidation';

interface Props {
  accountId: string;
  isWideMode: boolean;
  disabled?: boolean;
}

const Wrapper = styled(Box)`
  transition: all 0.3s ease;
  &:hover {
    ${({ theme }) => theme.shadows.hover};
  }
`;

const InvestmentsStartItem: React.FC<Props> = ({ accountId, isWideMode, disabled }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const { currentMyAccount: myAccount } = useMyEnrollments(accountId);
  const [errorWarningVisible, setErrorWarningVisible] = useState(false);
  const { isFullLiquidated } = useIsFullLiquidation({ accountId });
  const investor = useInvestor(accountId);
  const { handleSave: handleStartInvestments, isLoading: isStartInvestingLoading } = useInvestmentsStartQuery(
    {
      accountId: accountId,
      onError: () => {
        setErrorWarningVisible(true);
      },
    }
  );
  const { isVerificationRequired } = useCIPInfo();
  const isNotVerified = useMemo(
    () => isVerificationRequired && myAccount?.isCIPRequired,
    [isVerificationRequired, myAccount?.isCIPRequired]
  );
  const isAccountClosed = useMemo(() => {
    return myAccount?.accountStatus === AccountStatus.CLOSED;
  }, [myAccount?.accountStatus]);
  const zeroState = useMemo(
    () => myAccount && myAccount?.available < myAccount?.investmentThreshold,
    [myAccount]
  );
  const handleSaveInvestment = useCallback(() => {
    if (isFullLiquidated && accountId) {
      history.push(`${ROUTES.INVESTMENTS}?id=${accountId}`);
      return;
    }
    handleStartInvestments();
  }, [isFullLiquidated, accountId, handleStartInvestments, history]);
  const isInvestmentsDisabled = useMemo(() => {
    if (isNotVerified) {
      return true;
    }
    const availableBalance = myAccount?.available || 0;
    const minimumInvestmentAmount = myAccount?.minimumInvestmentAmount || 0;
    const investmentThreshold = myAccount?.investmentThreshold || 0;
    const total = minimumInvestmentAmount + investmentThreshold;
    return total > availableBalance;
  }, [
    isNotVerified,
    myAccount?.available,
    myAccount?.investmentThreshold,
    myAccount?.minimumInvestmentAmount,
  ]);

  const investmentHintContent = useMemo(() => {
    if (isNotVerified) {
      return (
        <Inscription size="small" textAlign="center">
          {t('We need to verify your identity to establish your HSA.')}
        </Inscription>
      );
    }
    if (isInvestmentsDisabled) {
      return (
        <Inscription textAlign="center" size="small">
          {t(`A minimum investment of  ${costFormater(
            myAccount?.minimumInvestmentAmount || 0
          )} is required to setup an investment account.
            You may start investing once your HSA has
            ${costFormater(
              (myAccount?.minimumInvestmentAmount || 0) + (myAccount?.investmentThreshold || 0)
            )}.
          `)}
        </Inscription>
      );
    }
    if (isLimitedAccess) {
      return (
        <Inscription size="small" textAlign="center">
          {t('Complete your setup to gain full access to your account.')}
        </Inscription>
      );
    }
    if (myAccount?.accountStatus === AccountStatus.CLOSED) {
      return (
        <Inscription size="small" textAlign="center">
          {t('Investments are not possible for closed account')}
        </Inscription>
      );
    }
    if (zeroState && myAccount?.accountStatus === AccountStatus.OPEN) {
      return (
        <Inscription size="small" textAlign="center">
          {t('Almost there! To start investing you need a minimum of ')}
          <Text style={{ lineHeight: 'inherit' }}>{costFormater(myAccount?.investmentThreshold)}</Text>
          {t(` in your ${myAccount?.accountType} account.`)}
        </Inscription>
      );
    }
    if (myAccount?.accountStatus === AccountStatus.PENDING) {
      return (
        <Inscription size="small" textAlign="center">
          {t('To start investing you need to verify your identity.')}
        </Inscription>
      );
    }
    return '';
  }, [
    isInvestmentsDisabled,
    isLimitedAccess,
    isNotVerified,
    myAccount?.accountStatus,
    myAccount?.accountType,
    myAccount?.investmentThreshold,
    myAccount?.minimumInvestmentAmount,
    t,
    zeroState,
  ]);

  const isInvestmentWithHint = useMemo(
    () =>
      isLimitedAccess ||
      isInvestmentsDisabled ||
      (zeroState && myAccount?.accountStatus === AccountStatus.OPEN) ||
      myAccount?.accountStatus === AccountStatus.PENDING,
    [isInvestmentsDisabled, isLimitedAccess, myAccount, zeroState]
  );

  const handleClick = useCallback(() => {
    if (!investor.data.type || investor?.data?.status === InvestorStatus.INVESTOR_CREATED) {
      handleSaveInvestment();
      return;
    }
    if (!isWideMode) {
      handleStartInvestments();
    }
  }, [handleSaveInvestment, handleStartInvestments, investor.data?.status, investor.data.type, isWideMode]);
  const isInvestmentButtonDisabled = useMemo(
    () =>
      !myAccount?.allowInvestments ||
      zeroState ||
      myAccount?.accountStatus === AccountStatus.PENDING ||
      isLimitedAccess ||
      isInvestmentsDisabled ||
      !!isNotVerified ||
      !!isAccountClosed,

    [
      isAccountClosed,
      myAccount?.allowInvestments,
      myAccount?.accountStatus,
      zeroState,
      isLimitedAccess,
      isInvestmentsDisabled,
      isNotVerified,
    ]
  );

  const childrenNode = useMemo(() => {
    return !investor?.data?.status || investor?.data?.status === InvestorStatus.INVESTOR_CREATED ? null : (
      <InvestmentResumeBlock accountId={accountId} isWideMode={isWideMode} />
    );
  }, [accountId, investor?.data?.status, isWideMode]);

  const hideInvestmentItem = useMemo(
    () =>
      investor?.data?.status === InvestorStatus.ACTIVE ||
      investor?.data?.status === InvestorStatus.PENDING_EXTERNAL_CREATION ||
      !myAccount?.allowInvestments,
    [investor, myAccount]
  );

  const isButtonDisabled = useMemo(() => {
    return !!isInvestmentButtonDisabled || isStartInvestingLoading || disabled;
  }, [disabled, isInvestmentButtonDisabled, isStartInvestingLoading]);
  const { isMobile } = useResponsive();
  if (hideInvestmentItem && !isNotVerified) {
    return null;
  }

  return (
    <Box flex>
      <RawButton onClick={isButtonDisabled ? undefined : handleClick}>
        <Wrapper
          data-testid="funds-management-item"
          direction={isMobile ? 'column' : isWideMode ? 'row' : 'column'}
          pad="spacing24"
          background="canvas"
          border={{ color: 'border2', size: '1px' }}
          style={{
            cursor: isButtonDisabled || isNotVerified || isAccountClosed ? 'not-allowed' : undefined,
          }}
          round="container1Round"
          justify="between"
        >
          <Box direction="row" fill={isWideMode} align={isWideMode ? 'center' : ''} justify="between">
            <Box direction="row" align="center" fill>
              <Box
                data-testid="funds-management-item-icon"
                margin={{ right: 'spacing16' }}
                width={{ min: 'min-content' }}
              >
                {isInvestmentButtonDisabled || isNotVerified || isAccountClosed ? (
                  <BigDisabledAddInvestmentIcon />
                ) : (
                  <BigAddInvestmentIcon />
                )}
              </Box>

              <Box>
                <Inscription
                  data-testid="funds-management-item-title"
                  size="16px"
                  weight={700}
                  lineHeight="22px"
                  margin={{ bottom: 'spacing4' }}
                  data-style="ItemTitle"
                >
                  {t('Investments')}
                </Inscription>

                <Text
                  data-testid="funds-management-item-description"
                  color="textSecondary"
                  style={{
                    lineHeight: '20px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  weight={400}
                >
                  {isInvestmentButtonDisabled || isNotVerified
                    ? t('Unavailable for now')
                    : t('You can now start investing!')}
                </Text>
              </Box>
            </Box>

            {isInvestmentWithHint || !!isNotVerified || !!isAccountClosed ? (
              <Box
                style={{ flex: 1 }}
                direction="row"
                align="center"
                justify="end"
                margin={{ right: 'l' }}
                pad={{ top: 'spacing4' }}
              >
                <Hint
                  hintWidth={180}
                  disabled={isButtonDisabled}
                  hintElement={<WarningIcon style={{ fill: baseTheme.colors.warning }} />}
                >
                  {investmentHintContent}
                </Hint>
              </Box>
            ) : (
              <>
                {!isWideMode && (
                  <Box style={{ flex: 1 }} align="end">
                    <Box width="24px" align="center" justify="center" height="24px">
                      <ChevronRightIcon color="iconPrimary" />
                    </Box>
                  </Box>
                )}
              </>
            )}
            <ServerErrorModal
              visible={errorWarningVisible}
              onSetVisible={setErrorWarningVisible}
              onSendRequest={handleStartInvestments}
              isLoading={isStartInvestingLoading}
            />

            {!isInvestmentWithHint &&
              (childrenNode === null || childrenNode === undefined) &&
              isWideMode &&
              !isAccountClosed && (
                <div>
                  <Box align="center" justify="center">
                    <Hint
                      hideHint={!(isInvestmentWithHint || !!isNotVerified)}
                      hintWidth={240}
                      disabled={isButtonDisabled}
                      hintElement={
                        <Box>
                          <AppButton
                            testId="funds-management-item"
                            width="180px"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleClick();
                            }}
                            disabled={isButtonDisabled || isInvestmentsDisabled}
                          >
                            {isStartInvestingLoading ? <Preloader /> : t('Start Investing')}
                          </AppButton>
                        </Box>
                      }
                    >
                      {investmentHintContent}
                    </Hint>
                  </Box>
                </div>
              )}
          </Box>
          {childrenNode}
        </Wrapper>
      </RawButton>
    </Box>
  );
};
export default InvestmentsStartItem;
